import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { http, formatApiError, toastApiError, downloadUrl, getUserLabels } from "../lib/api";
import { recordLink } from "../lib/notify";
import { useNavDraft } from "../lib/navDraft";
import { fmtTL, fmtDate, todayIso, fmtCreator } from "../lib/format";
import { formKeyNav } from "../lib/formNav";
import { toast } from "../lib/notify";
import { Plus, Pencil, Trash2, Save, X, CheckCircle2, RotateCcw, Landmark, FileDown, FileUp } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import UploadErrorsButton from "../components/projects/UploadErrorsButton";
import { FilterTh, useColFilters } from "../components/ColFilters";
import AmountInput, { parseTL } from "../components/AmountInput";
import { useRecordFlash } from "../lib/flash";
import { FilterTotal } from "../components/ColFilters";
import SearchableSelect from "../components/SearchableSelect";
import { readJson, removeKey, writeJson } from "../lib/storage";

const CHECK_COLS = [
  { key: "company", label: "Firma" },
  { key: "bank", label: "Banka" },
  { key: "check_no", label: "Çek No", type: "text" },
  { key: "due_date", label: "Ödeme Tarihi", type: "datemulti", fmt: fmtDate },
  { key: "given_to", label: "Kime Verildi" },
  { key: "ciro_to", label: "Ciro Bilgisi", type: "text" },
  { key: "amount", label: "Tutar", type: "number" },
  { key: "description", label: "Açıklama", type: "text" },
  { key: "durum", label: "Durum", options: [{ value: "Açık", label: "Açık" }, { value: "Ödendi", label: "Ödendi" }], value: (c) => (c.paid ? "Ödendi" : "Açık") },
];

const SUM_AMOUNT = [{ key: "amount", label: "Tutar" }];
const RECENT_HEAD = <>{CHECK_COLS.map((c, i) => <th key={c.key} className={`px-3 py-1.5 font-semibold ${i === 6 ? "text-right" : "text-left"}`}>{c.label}</th>)}<th className="px-3 py-1.5 text-left font-semibold">Oluşturan</th><th /></>;
const emptyForm = { company: "", bank: "", check_no: "", due_date: todayIso(), given_to: "", ciro_to: "", amount: "", description: "" };
const cls = "w-full px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none";

export default function Checks() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const draftRestoreRef = useRef(false);
  const [checks, setChecks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const load = () => http.get("/checks").then((r) => setChecks(r.data));
  useEffect(() => {
    const nc = location.state?.newCari;
    if (!nc || draftRestoreRef.current) return;
    draftRestoreRef.current = true;
    const draft = readJson("check-form-draft", null);
    removeKey("check-form-draft");
    setForm({ ...(draft?.form || emptyForm), [draft?.target || "given_to"]: nc });
    setEditing(draft?.editing || "new");
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [location.state, location.pathname, location.search, navigate]);
  // son eklenen 4 çek (FilterTotal "son kayıtlar" paneli)
  const recentRows = useMemo(() => [...checks].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")), [checks]);
  useNavDraft("cek", () => (editing ? { editing, form } : null), (d) => { setEditing(d.editing); setForm(d.form); });
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);   // hatalı satırları Excel indirmek için
  useRecordFlash([checks.length]);
  const [userLabels, setUserLabels] = useState({});
  useEffect(() => { getUserLabels().then(setUserLabels); }, []);
  const [banks, setBanks] = useState([]);
  useEffect(() => { http.get("/banks").then((r) => setBanks(r.data)).catch(() => {}); }, []);
  const [caris, setCaris] = useState([]);
  useEffect(() => { http.get("/caris").then((r) => setCaris(r.data)).catch(() => {}); }, []);
  const cariNames = [...new Set(caris.map((c) => c.name).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr"));
  // "Yeni Cari Aç": form taslağı saklanır, cari sayfasından dönüşte ilgili alana yazılır
  const newCariFooter = useCallback((target, testid) => ({
    label: "Yeni Cari Aç", testid,
    onClick: () => {
      writeJson("check-form-draft", { form, editing, target });
      navigate("/cariler?yeni=1", { state: { from: "/veri-girisi?bolum=cek" } });
    },
  }), [form, editing, navigate]);
  const givenToFooter = useMemo(() => newCariFooter("given_to", "check-cari-new"), [newCariFooter]);
  const ciroFooter = useMemo(() => newCariFooter("ciro_to", "check-ciro-cari-new"), [newCariFooter]);
  const normName = (s) => (s || "").trim().toLocaleUpperCase("tr").replace(/\s+/g, " ");
  const ALIASES = { "WILCO AŞ.": ["WILCO MERKEZ"] };
  const ownNames = new Set([normName(form.company), ...(ALIASES[normName(form.company)] || [])].filter(Boolean));
  const notOwn = (n) => !ownNames.has(normName(n));
  const cariOptions = (form.given_to && !cariNames.includes(form.given_to) ? [form.given_to, ...cariNames] : cariNames).filter(notOwn).map((n) => ({ value: n, label: n }));
  const ciroOptions = (form.ciro_to && !cariNames.includes(form.ciro_to) ? [form.ciro_to, ...cariNames] : cariNames).filter((n) => n !== form.given_to && notOwn(n)).map((n) => ({ value: n, label: n }));
  const bankNames = [...new Set(banks.map((b) => b.name).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr"));
  const bankOptions = form.bank && !bankNames.includes(form.bank) ? [form.bank, ...bankNames] : bankNames;
  const companyNames = [...new Set(banks.map((b) => b.company).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr"));
  const companyOptions = form.company && !companyNames.includes(form.company) ? [form.company, ...companyNames] : companyNames;
  const fileRef = useRef(null);

  const uploadExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await http.post("/checks/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadResult(r.data); setUploadFile(file);
      if (r.data.created > 0) toast.success(`${r.data.created} çek içe aktarıldı`);
      if (r.data.errors?.length > 0) toast.warning(`${r.data.errors.length} satır hatalı`);
      await load();
    } catch (err) { toastApiError(err); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNew = () => { setForm(emptyForm); setEditing("new"); };
  const startEdit = (c) => { setForm({ ...emptyForm, ...c }); setEditing(c.id); };
  const cancel = () => { setEditing(null); setForm(emptyForm); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: parseTL(form.amount) ?? 0 };
      if (!payload.given_to?.trim()) return toast.error("Kime Verildi (cari) seçimi zorunludur");
      if (!(payload.amount > 0)) return toast.error("Çek tutarı zorunludur");
      const saved = editing === "new" ? (await http.post("/checks", payload)).data : (await http.put(`/checks/${editing}`, payload)).data;
      toast.success("Çek kaydedildi — vade tarihinde Ödeme Planı'na düşer", { link: recordLink(editing === "new" ? saved?.id : editing) });
      cancel(); await load();
    } catch (err) { toastApiError(err); }
  };

  const pay = async (id) => { try { await http.patch(`/checks/${id}/pay`); toast.success("Ödendi işaretlendi"); await load(); } catch (e) { toastApiError(e); } };
  const unpay = async (id) => { try { await http.patch(`/checks/${id}/unpay`); toast.success("Ödeme geri alındı"); await load(); } catch (e) { toastApiError(e); } };
  const remove = async (id) => { if (!window.confirm("Çek silinsin mi?")) return; try { await http.delete(`/checks/${id}`); toast.success("Silindi"); await load(); } catch (e) { toastApiError(e); } };

  const openTotal = checks.filter((c) => !c.paid).reduce((s, c) => s + (c.amount || 0), 0);

  const visibleChecks = [...checks]
    .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || "") || String(a.check_no).localeCompare(String(b.check_no), "tr"));
  const [hf, setHf, visRows, autoOpts] = useColFilters(visibleChecks, CHECK_COLS);

  const checkCells = (c) => (
    <>
      <td className="px-3 py-2 text-xs font-medium">{c.company}</td>
      <td className="px-3 py-2 text-xs">{c.bank}</td>
      <td className="px-3 py-2 text-xs font-mono">{c.check_no}</td>
      <td className="px-3 py-2 text-xs">{fmtDate(c.due_date)}</td>
      <td className="px-3 py-2 text-xs">{c.given_to}</td>
      <td className="px-3 py-2 text-xs">{c.ciro_to || "—"}</td>
      <td className="numeric-cell px-3 py-2 text-xs font-semibold">{fmtTL(c.amount)}</td>
      <td className="px-3 py-2 text-xs text-slate-500">{c.description || "—"}</td>
      <td className="px-3 py-2 text-xs">
        {c.paid ? (
          <span className="inline-flex px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold uppercase tracking-wider" data-testid={`check-status-${c.id}`}>Ödendi</span>
        ) : (
          <span className="inline-flex px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wider" data-testid={`check-status-${c.id}`}>Açık</span>
        )}
      </td>
      <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap" data-testid={`check-creator-${c.id}`}>{fmtCreator(c.created_by, c.created_at, userLabels)}</td>
    </>
  );
  const rowActions = (c) => (
    <>
      {c.paid ? (
        <button onClick={() => unpay(c.id)} title="Ödemeyi geri al" className="text-amber-700 hover:text-amber-900 mr-2" data-testid={`check-unpay-${c.id}`}><RotateCcw className="w-3.5 h-3.5" /></button>
      ) : (
        <button onClick={() => pay(c.id)} title="Ödendi işaretle" className="text-emerald-700 hover:text-emerald-900 mr-2" data-testid={`check-pay-${c.id}`}><CheckCircle2 className="w-3.5 h-3.5" /></button>
      )}
      {isAdmin && !c.paid && (
        <button onClick={() => startEdit(c)} title="Düzenle (yalnız ödenmemiş çek, yönetici)" className="text-slate-600 hover:text-slate-900 mr-2" data-testid={`check-edit-${c.id}`}><Pencil className="w-3.5 h-3.5" /></button>
      )}
      {isAdmin && (
        <button onClick={() => remove(c.id)} className="text-red-700 hover:text-red-900" data-testid={`check-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
      )}
    </>
  );

  const recent = { rows: recentRows, count: 4, head: RECENT_HEAD, render: (c) => <>{checkCells(c)}<td className="px-3 py-2 text-right whitespace-nowrap">{rowActions(c)}</td></> };
  return (
    <div className="space-y-6" data-testid="page-checks">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Landmark className="w-7 h-7 text-amber-500" /> Verilen Çekler
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Çekler vade (ödeme) tarihine göre Ödeme Planı'na otomatik düşer ve sarı satırlarla gösterilir. Açık çek toplamı: <span className="font-semibold text-amber-700" data-testid="checks-open-total">{fmtTL(openTotal)}</span>
          </p>
        </div>
        {!editing && (
          <div className="flex items-center gap-2 flex-wrap">
            <a href={downloadUrl("/checks/template")} target="_blank" rel="noreferrer" data-testid="check-template"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <FileDown className="w-4 h-4" /> Şablon İndir
            </a>
            <button onClick={() => fileRef.current?.click()} data-testid="check-upload"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-emerald-600 bg-emerald-50 text-sm font-semibold text-emerald-800 hover:bg-emerald-100">
              <FileUp className="w-4 h-4" /> Excel'den Yükle
            </button>
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" data-testid="check-upload-input" onChange={uploadExcel} />
            <button onClick={startNew} data-testid="check-add" className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
              <Plus className="w-4 h-4" /> Yeni Çek
            </button>
          </div>
        )}
      </div>

      {uploadResult && (
        <div className={`rounded-md border px-4 py-3 text-xs ${uploadResult.errors?.length ? "bg-amber-50 border-amber-300" : "bg-emerald-50 border-emerald-300"}`} data-testid="check-upload-result">
          <div className="flex items-center justify-between">
            <span className="font-semibold">{uploadResult.created} çek aktarıldı{uploadResult.errors?.length ? `, ${uploadResult.errors.length} satır hatalı` : ""} — sütunlar başlık adından eşleşir, sıra önemli değildir</span>
            <div className="flex items-center gap-2">
              <UploadErrorsButton file={uploadFile} errors={uploadResult.errors} name="Çek" testid="check-upload-errors-xlsx" />
              <button onClick={() => setUploadResult(null)} className="text-slate-500 hover:text-slate-800" data-testid="check-upload-result-close"><X className="w-4 h-4" /></button>
            </div>
          </div>
          {uploadResult.errors?.slice(0, 10).map((er, i) => (
            <div key={`${er.row}-${er.message}`} className="mt-1 text-red-700" data-testid={`check-upload-err-${i}`}>Satır {er.row}: {er.message}</div>
          ))}
        </div>
      )}

      {editing && (
        <form onSubmit={submit} onKeyDown={formKeyNav} className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="check-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <F label="Firma">
              <select required data-testid="check-field-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={cls}>
                <option value="">Seçiniz…</option>
                {companyOptions.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </F>
            <F label="Banka">
              <select required data-testid="check-field-bank" value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} className={cls}>
                <option value="">Seçiniz…</option>
                {bankOptions.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              {bankNames.length === 0 && <span className="block text-[11px] text-amber-700 mt-1">Liste boş — Sabit Tanımlar → Bankalar bölümünden banka ekleyin</span>}
            </F>
            <F label="Çek No"><input required data-testid="check-field-no" value={form.check_no} onChange={(e) => setForm({ ...form, check_no: e.target.value })} className={cls} /></F>
            <F label="Ödeme Tarihi (Vade)"><input required type="date" data-testid="check-field-due" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={cls} /></F>
            <F label="Kime Verildi (Cari)">
              <SearchableSelect testid="check-field-given-to" options={cariOptions} value={form.given_to} onChange={(v) => setForm({ ...form, given_to: v, ciro_to: form.ciro_to === v ? "" : form.ciro_to })} placeholder="Cari seçin…"
                footer={givenToFooter} />
              {cariNames.length === 0 && <span className="block text-[11px] text-amber-700 mt-1">Liste boş — Cari Listesi menüsünden cari ekleyin</span>}
            </F>
            <F label="Ciro Bilgisi (opsiyonel)">
              <SearchableSelect testid="check-field-ciro-to" options={ciroOptions} value={form.ciro_to} onChange={(v) => setForm({ ...form, ciro_to: v })} placeholder="Ciro edilen cari…"
                footer={ciroFooter} />
            </F>
            <F label="Tutar (TL)"><AmountInput required testid="check-field-amount" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} className={cls} /></F>
            <F label="Açıklama"><input data-testid="check-field-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={cls} /></F>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={cancel} className="inline-flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900"><X className="w-4 h-4" /> İptal</button>
            <button type="submit" data-testid="check-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"><Save className="w-4 h-4" /> Kaydet</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <FilterTotal rows={visRows} sums={SUM_AMOUNT} testid="checks-filter-total" recent={recent} />
        <table className="w-full tbl">
          <thead className="bg-slate-50">
            <tr>
              {CHECK_COLS.map((c, i) => (
                <th key={c.key} className={`px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold ${i === 6 ? "text-right" : "text-left"}`}>
                  <FilterTh col={c} hf={hf} setHf={setHf} testid={`check-hf-${c.key}`} align={i === 6 ? "right" : "left"} autoOptions={autoOpts[c.key]} />
                </th>
              ))}
              <th className="px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold text-left">Oluşturan</th>
              <th className="px-3 py-2.5 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {visRows.map((c) => (
              <tr key={c.id} data-flash-id={c.id} className="border-t border-slate-100" data-testid={`check-row-${c.id}`}>
                {checkCells(c)}
                <td className="px-3 py-2 text-right whitespace-nowrap">{rowActions(c)}</td>
              </tr>
            ))}
            {visRows.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-sm text-slate-500" data-testid="checks-empty">{checks.length === 0 ? "Kayıtlı çek yok" : "Filtrelerle eşleşen çek yok"}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      {children}
    </div>
  );
}
